// JourneyPlannerPage Component
// =============================
// Main page for creating learning plans from materials via Anna chat
// Feature #83 - Journey planner with material upload, Anna chat, and plan preview

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../layout/Layout';
import Card from '../ui/Card';
import Button from '../ui/Button';
import MaterialUpload from './MaterialUpload';
import JourneyAnnaChat from './JourneyAnnaChat';
import EditPlanAnnaChat from './EditPlanAnnaChat';
import LearningPlanPreview from './LearningPlanPreview';
import { useAuth } from '../../contexts/AuthContext';
import { useLearningJourneys } from '../../hooks/useLearningJourneys';
import { useJourneyMaterials } from '../../hooks/useJourneyMaterials';
import { useJourneyTopics } from '../../hooks/useJourneyTopics';
import { readFileContent } from '../../../utils/fileUtils';
import type { SuggestedLearningPlan, SuggestedPlanTopic } from '../../services/annaTextChat';
import type { LearningJourney, Topic, Subtopic } from '../../types';

// View states for the planner
type PlannerView = 'upload' | 'chat' | 'preview' | 'edit-choice' | 'edit-chat';

const JourneyPlannerPage: React.FC = () => {
  const { t } = useTranslation(['library', 'common']);
  const { journeyId } = useParams<{ journeyId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Hooks
  const { getJourneyById, updateJourney, loading: journeysLoading } = useLearningJourneys();
  const {
    materials,
    loading: materialsLoading,
    uploading,
    fetchMaterials,
    uploadMaterial,
    deleteMaterial,
    updateExtractedText,
  } = useJourneyMaterials();
  const { topics: existingTopics, addTopicsBatch, deleteAllTopics } = useJourneyTopics(journeyId);

  // Local state
  const [journey, setJourney] = useState<LearningJourney | null>(null);
  const [currentView, setCurrentView] = useState<PlannerView>('upload');
  const [currentPlan, setCurrentPlan] = useState<SuggestedLearningPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isApproving, setIsApproving] = useState(false);

  // Check if journey has an approved learning plan
  const hasApprovedPlan = journey?.status === 'active' && (journey?.topicIds?.length ?? 0) > 0;

  // Feature #215: Filter materials to only show truly new ones (not already analyzed)
  // Must be called before any early returns to maintain hook order
  const trulyNewMaterials = useMemo(() => {
    const analyzedIds = journey?.analyzedMaterialIds || [];
    return materials.filter(m => !analyzedIds.includes(m.id));
  }, [materials, journey?.analyzedMaterialIds]);

  // Load journey and set initial view based on plan status
  useEffect(() => {
    if (journeyId) {
      const j = getJourneyById(journeyId);
      if (j) {
        setJourney(j);
        // If journey has approved plan, show edit choice first
        if (j.status === 'active' && (j.topicIds?.length ?? 0) > 0) {
          setCurrentView('edit-choice');
        }
      }
    }
  }, [journeyId, getJourneyById]);

  // Load materials when journey changes
  useEffect(() => {
    if (journeyId) {
      fetchMaterials(journeyId);
    }
  }, [journeyId, fetchMaterials]);

  // Handle file upload with text extraction
  const handleUpload = useCallback(async (file: File) => {
    if (!journeyId) return { success: false, error: t('library:journeyPlanner.upload.noJourneyError') };

    // Upload the file
    const result = await uploadMaterial(journeyId, file);

    if (result.success && result.material) {
      // Extract text from the file
      try {
        const text = await readFileContent(file);
        if (text) {
          await updateExtractedText(result.material.id, text);
        }
      } catch (extractError) {
        console.warn('[JourneyPlanner] Could not extract text:', extractError);
        // Still return success - file was uploaded, just couldn't extract text
      }
    }

    return result;
  }, [journeyId, uploadMaterial, updateExtractedText, t]);

  // Handle delete
  const handleDelete = useCallback(async (materialId: string) => {
    return deleteMaterial(materialId);
  }, [deleteMaterial]);

  // Handle plan approval from Anna chat
  const handlePlanApproved = useCallback((plan: SuggestedLearningPlan) => {
    setCurrentPlan(plan);
    setCurrentView('preview');
  }, []);

  // Convert suggested plan topics to Topic format
  const convertPlanToTopics = useCallback((plan: SuggestedLearningPlan): Omit<Topic, 'id'>[] => {
    return plan.topics.map((planTopic: SuggestedPlanTopic, idx: number) => {
      // Convert suggested subtopics to Subtopic format
      const subtopics: Subtopic[] = (planTopic.subtopics || []).map((sub, subIdx) => ({
        id: `subtopic_${Date.now()}_${idx}_${subIdx}`,
        title: sub.title,
        concepts: sub.concepts || [],
        isCompleted: false,
        completed: false,
        order: subIdx,
      }));

      return {
        title: planTopic.title,
        userId: user?.uid || '',
        journeyId: journeyId || '',
        description: planTopic.description,
        keyConcepts: planTopic.keyConcepts?.map(c => ({ term: c, definition: '' })),
        estimatedDuration: planTopic.estimatedDuration,
        subtopics,
        order: idx,
        completedSessions: 0,
        totalEstimatedSessions: Math.max(1, subtopics.length),
      };
    });
  }, []);

  // Handle final plan approval (from preview) - Feature #86, #102
  const handleFinalApproval = useCallback(async (plan: SuggestedLearningPlan) => {
    if (!journeyId) return;

    setIsApproving(true);
    setError(null);

    try {
      // If editing existing plan, delete old topics first
      if (hasApprovedPlan && existingTopics.length > 0) {
        const deleteResult = await deleteAllTopics();
        if (!deleteResult.success) {
          console.warn('[JourneyPlanner] Could not delete all old topics:', deleteResult.error);
          // Continue anyway - we'll create new topics
        }
      }

      // Convert plan to topics
      const topicsData = convertPlanToTopics(plan);

      // Create topics in Firestore
      const result = await addTopicsBatch(topicsData);

      if (!result.success) {
        setError(result.error || t('library:journeyPlanner.errors.couldNotCreateTopics'));
        setIsApproving(false);
        return;
      }

      // Update journey with topic IDs, set status to active, and track analyzed materials (Feature #180)
      if (result.ids) {
        const analyzedMaterialIds = materials.map(m => m.id);
        await updateJourney(journeyId, {
          topicIds: result.ids,
          status: 'active',
          analyzedMaterialIds, // Track which materials were used for this plan
        });
      }

      // Navigate to journey detail page
      navigate(`/journeys/${journeyId}`);

    } catch (err) {
      console.error('[JourneyPlanner] Error approving plan:', err);
      setError(t('library:journeyPlanner.errors.approvalError'));
    } finally {
      setIsApproving(false);
    }
  }, [journeyId, hasApprovedPlan, existingTopics.length, deleteAllTopics, convertPlanToTopics, addTopicsBatch, updateJourney, navigate, materials, t]);

  // Return to chat from preview
  const handleEditWithAnna = useCallback(() => {
    setCurrentView('chat');
  }, []);

  // Proceed to chat after uploading materials
  const handleProceedToChat = useCallback(() => {
    if (materials.length === 0) {
      setError(t('library:journeyPlanner.upload.noFilesError'));
      return;
    }
    setCurrentView('chat');
  }, [materials.length, t]);

  // Loading state
  if (journeysLoading || !journey) {
    return (
      <Layout>
        <div className="bg-gradient-to-br from-white via-primary-50/30 to-indigo-50/20 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-neutral-medium">{t('common:status.loading')}</p>
            </div>
          </div>
        </div>
        </div>
      </Layout>
    );
  }

  // Check if we have materials with extracted text
  const hasMaterialsWithText = materials.some(m => m.extractedText);

  return (
    <Layout>
      <div className="bg-gradient-to-br from-white via-primary-50/30 to-indigo-50/20 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <ol className="flex items-center gap-2 text-sm">
            <li>
              <Link
                to="/sessions"
                className="text-neutral-medium hover:text-primary-600 transition-colors"
              >
                {t('library:breadcrumb')}
              </Link>
            </li>
            <li className="text-neutral-medium">/</li>
            <li>
              <Link
                to={`/journeys/${journeyId}`}
                className="text-neutral-medium hover:text-primary-600 transition-colors"
              >
                {journey.subject}
              </Link>
            </li>
            <li className="text-neutral-medium">/</li>
            <li className="text-neutral-dark dark:text-white font-medium">{t('common:topic.plan')}</li>
          </ol>
        </nav>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-neutral-dark dark:text-white mb-2">
            {hasApprovedPlan
              ? t('library:journeyPlanner.headerEdit', { subject: journey.subject })
              : t('library:journeyPlanner.headerCreate', { subject: journey.subject })}
          </h1>
          <p className="text-neutral-medium">
            {hasApprovedPlan
              ? t('library:journeyPlanner.descriptionEdit')
              : t('library:journeyPlanner.descriptionCreate')}
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <button
              onClick={() => setError(null)}
              className="mt-2 text-sm text-red-500 hover:text-red-700"
            >
              {t('common:actions.close')}
            </button>
          </div>
        )}

        {/* Step indicator - hide for edit-choice and edit-chat views */}
        {currentView !== 'edit-choice' && currentView !== 'edit-chat' && (
        <div className="mb-8">
          <div className="flex items-center justify-center gap-4">
            {(['upload', 'chat', 'preview'] as PlannerView[]).map((step, idx) => (
              <React.Fragment key={step}>
                {idx > 0 && (
                  <div
                    className={`h-0.5 w-16 ${
                      (['upload', 'chat', 'preview'] as PlannerView[]).indexOf(currentView) >= idx
                        ? 'bg-primary-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  />
                )}
                <button
                  onClick={() => {
                    // Allow going back to previous steps
                    if (idx <= ['upload', 'chat', 'preview'].indexOf(currentView)) {
                      setCurrentView(step);
                    }
                  }}
                  disabled={idx > ['upload', 'chat', 'preview'].indexOf(currentView)}
                  className={`flex items-center gap-2 transition-colors ${
                    ['upload', 'chat', 'preview'].indexOf(currentView) >= idx
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-400'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-bold border-2 ${
                      currentView === step
                        ? 'bg-primary-500 border-primary-500 text-white'
                        : ['upload', 'chat', 'preview'].indexOf(currentView) > idx
                        ? 'bg-primary-100 dark:bg-primary-900/30 border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-400'
                    }`}
                  >
                    {idx + 1}
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">
                    {step === 'upload' && t('library:journeyPlanner.steps.upload')}
                    {step === 'chat' && t('library:journeyPlanner.steps.chatWithAnna')}
                    {step === 'preview' && t('library:journeyPlanner.steps.approve')}
                  </span>
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
        )}

        {/* Content based on current view */}

        {/* Edit choice view - shown when journey has approved plan */}
        {currentView === 'edit-choice' && (
          <div className="max-w-2xl mx-auto">
            <Card padding="lg">
              <div className="text-center mb-6">
                <span className="text-3xl mb-4 block">📋</span>
                <h2 className="text-lg font-semibold text-neutral-dark dark:text-white mb-2">
                  {t('library:journeyPlanner.editChoice.planActive')}
                </h2>
                <p className="text-neutral-medium">
                  {t('library:journeyPlanner.editChoice.planActiveDescription', {
                    count: journey.topicIds?.length || 0,
                    subject: journey.subject,
                  })}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  onClick={() => setCurrentView('upload')}
                  className="flex flex-col items-center gap-3 p-6 glass-card hover:border-primary-500 border-2 border-transparent transition-all group"
                >
                  <span className="text-4xl group-hover:scale-110 transition-transform">📚</span>
                  <div className="text-center">
                    <h3 className="font-medium text-neutral-dark dark:text-white mb-1">
                      {t('library:journeyPlanner.editChoice.addMaterial')}
                    </h3>
                    <p className="text-sm text-neutral-medium">
                      {t('library:journeyPlanner.editChoice.addMaterialDescription')}
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => setCurrentView('edit-chat')}
                  className="flex flex-col items-center gap-3 p-6 glass-card hover:border-primary-500 border-2 border-transparent transition-all group"
                >
                  <span className="text-4xl group-hover:scale-110 transition-transform">💬</span>
                  <div className="text-center">
                    <h3 className="font-medium text-neutral-dark dark:text-white mb-1">
                      {t('library:journeyPlanner.editChoice.talkToAnna')}
                    </h3>
                    <p className="text-sm text-neutral-medium">
                      {t('library:journeyPlanner.editChoice.talkToAnnaDescription')}
                    </p>
                  </div>
                </button>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
                <Link
                  to={`/journeys/${journeyId}`}
                  className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  {t('library:journeyPlanner.editChoice.backToJourney')}
                </Link>
              </div>
            </Card>
          </div>
        )}

        {currentView === 'upload' && (
          <div className="max-w-2xl mx-auto">
            <Card padding="lg">
              <h2 className="text-lg font-semibold text-neutral-dark dark:text-white mb-4">
                {t('library:journeyPlanner.upload.heading')}
              </h2>
              <p className="text-sm text-neutral-medium mb-6">
                {t('library:journeyPlanner.upload.description')}
              </p>

              <MaterialUpload
                journeyId={journeyId!}
                materials={materials}
                uploading={uploading}
                onUpload={handleUpload}
                onDelete={handleDelete}
                disabled={materialsLoading}
              />

              <div className="mt-6 flex justify-end">
                <Button
                  variant="primary"
                  onClick={handleProceedToChat}
                  disabled={materials.length === 0 || uploading}
                >
                  {t('library:journeyPlanner.upload.continueToAnna')}
                </Button>
              </div>
            </Card>
          </div>
        )}

        {currentView === 'chat' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Materials sidebar */}
            <div className="lg:col-span-1">
              <Card padding="md">
                <h3 className="text-sm font-medium text-neutral-dark dark:text-white mb-3">
                  {t('library:journeyPlanner.sidebar.uploadedMaterials')}
                </h3>
                {materials.length > 0 ? (
                  <div className="space-y-2">
                    {materials.map((m) => (
                      <div
                        key={m.id}
                        className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded"
                      >
                        <span>
                          {m.fileType === 'pdf' ? '📕' : m.fileType === 'docx' ? '📘' : '📄'}
                        </span>
                        <span className="text-sm truncate flex-1">{m.fileName}</span>
                        {m.extractedText && (
                          <span className="text-xs text-green-600">✓</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-neutral-medium">{t('library:journeyPlanner.sidebar.noMaterials')}</p>
                )}

                <button
                  onClick={() => setCurrentView('upload')}
                  className="mt-4 text-sm text-primary-600 hover:text-primary-700"
                >
                  {t('library:journeyPlanner.sidebar.addMoreMaterials')}
                </button>
              </Card>

              {/* Feature #186: Show existing topics when editing existing plan */}
              {hasApprovedPlan && existingTopics.length > 0 && (
                <Card padding="md" className="mt-4">
                  <h3 className="text-sm font-medium text-neutral-dark dark:text-white mb-3">
                    {t('library:journeyPlanner.sidebar.existingTopics', { count: existingTopics.length })}
                  </h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {existingTopics.map((topic) => (
                      <div
                        key={topic.id}
                        className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800"
                      >
                        <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
                          {topic.title}
                        </span>
                        {topic.subtopics && topic.subtopics.length > 0 && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                            {t('library:journeyDetail.subtopicsCount', { count: topic.subtopics.length })}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>

            {/* Chat area */}
            <div className="lg:col-span-2 h-[600px]">
              {/* Feature #186: Use EditPlanAnnaChat when user has existing plan */}
              {/* This ensures EditPlanGeneratorService is used for better material integration */}
              {hasApprovedPlan ? (
                <EditPlanAnnaChat
                  journey={journey}
                  existingTopics={existingTopics}
                  newMaterials={materials}
                  analyzedMaterialIds={journey.analyzedMaterialIds || []}
                  onPlanApproved={handlePlanApproved}
                  onClose={() => setCurrentView('edit-choice')}
                />
              ) : (
                /* Feature #174: Pass existing topics for context-aware welcome message */
                /* Feature #180: Pass analyzed material IDs to determine new materials */
                <JourneyAnnaChat
                  journey={journey}
                  materials={materials}
                  existingTopics={existingTopics}
                  analyzedMaterialIds={journey.analyzedMaterialIds || []}
                  onPlanApproved={handlePlanApproved}
                />
              )}
            </div>
          </div>
        )}

        {currentView === 'preview' && currentPlan && (
          <div className="max-w-4xl mx-auto">
            <LearningPlanPreview
              plan={currentPlan}
              onApprove={handleFinalApproval}
              onEditWithAnna={handleEditWithAnna}
              onCancel={() => navigate('/library')}
              isApproving={isApproving}
            />
          </div>
        )}

        {/* Edit chat view - for editing existing plans (Feature #99, #102) */}
        {currentView === 'edit-chat' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Existing topics sidebar */}
            <div className="lg:col-span-1">
              <Card padding="md">
                <h3 className="text-sm font-medium text-neutral-dark dark:text-white mb-3">
                  {t('library:journeyPlanner.sidebar.currentTopics', { count: existingTopics.length })}
                </h3>
                {existingTopics.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {existingTopics.map((topic) => (
                      <div
                        key={topic.id}
                        className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800"
                      >
                        <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
                          {topic.title}
                        </span>
                        {topic.subtopics && topic.subtopics.length > 0 && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                            {t('library:journeyDetail.subtopicsCount', { count: topic.subtopics.length })}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-neutral-medium">{t('library:journeyPlanner.sidebar.noTopicsInPlan')}</p>
                )}

                <button
                  onClick={() => setCurrentView('edit-choice')}
                  className="mt-4 text-sm text-amber-600 hover:text-amber-700"
                >
                  {t('library:journeyPlanner.sidebar.backToChoice')}
                </button>
              </Card>

              {/* New materials if any - Feature #215: Only show truly new materials */}
              {trulyNewMaterials.length > 0 && (
                <Card padding="md" className="mt-4">
                  <h3 className="text-sm font-medium text-neutral-dark dark:text-white mb-3">
                    {t('library:journeyPlanner.sidebar.newMaterials')}
                  </h3>
                  <div className="space-y-2">
                    {trulyNewMaterials.map((m) => (
                      <div
                        key={m.id}
                        className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded"
                      >
                        <span>
                          {m.fileType === 'pdf' ? '📕' : m.fileType === 'docx' ? '📘' : '📄'}
                        </span>
                        <span className="text-sm truncate flex-1">{m.fileName}</span>
                        {m.extractedText && (
                          <span className="text-xs text-green-600">✓</span>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>

            {/* Edit chat area */}
            <div className="lg:col-span-2 h-[600px]">
              {/* Feature #180: Pass analyzedMaterialIds to distinguish new vs already-analyzed materials */}
              <EditPlanAnnaChat
                journey={journey}
                existingTopics={existingTopics}
                newMaterials={materials}
                analyzedMaterialIds={journey.analyzedMaterialIds || []}
                onPlanApproved={handlePlanApproved}
                onClose={() => setCurrentView('edit-choice')}
              />
            </div>
          </div>
        )}

        {/* Empty plan state in preview */}
        {currentView === 'preview' && !currentPlan && (
          <div className="max-w-2xl mx-auto">
            <Card padding="lg" className="text-center">
              <span className="text-3xl mb-4 block">📋</span>
              <h2 className="text-lg font-semibold text-neutral-dark dark:text-white mb-2">
                {t('library:journeyPlanner.emptyPlan.title')}
              </h2>
              <p className="text-neutral-medium mb-4">
                {t('library:journeyPlanner.emptyPlan.description')}
              </p>
              <Button variant="primary" onClick={() => setCurrentView('chat')}>
                {t('library:journeyPlanner.emptyPlan.backToAnna')}
              </Button>
            </Card>
          </div>
        )}

        {/* Info about materials */}
        {!hasMaterialsWithText && materials.length > 0 && currentView === 'chat' && (
          <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <div className="flex items-start gap-3">
              <span className="text-xl">⏳</span>
              <div>
                <p className="font-medium text-amber-800 dark:text-amber-300">
                  {t('library:journeyPlanner.textExtraction.inProgress')}
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  {t('library:journeyPlanner.textExtraction.description')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </Layout>
  );
};

export default JourneyPlannerPage;
